/**
 * Organization service
 * Handles organization CRUD operations and subscription management
 */

import {
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from './firebase';
import { Organization, SubscriptionSnapshot } from '@/types';
import { isActiveSubscription } from '@/utils/subscription';

/**
 * Create organization record in Firestore
 * Called after initial signup
 */
export async function createOrganization(
  orgId: string,
  name: string,
  email: string,
  adminPin: string
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);

  const organization: Organization = {
    id: orgId,
    name,
    email,
    adminPin,
    createdAt: Timestamp.now(),
    branding: {},
  };

  // Atomic: an org must never exist without its default questionnaire —
  // signup recovery treats "org exists" as "setup finished".
  const batch = writeBatch(db);
  batch.set(orgRef, organization);
  batch.set(
    doc(db, 'questionnaires', `${orgId}_default`),
    buildDefaultQuestionnaire(orgId)
  );
  await batch.commit();
}

/**
 * Default questionnaire for new organizations
 */
function buildDefaultQuestionnaire(orgId: string) {
  return {
    id: `${orgId}_default`,
    orgId,
    title: 'Guest Information',
    isActive: true,
    questions: [
      {
        id: 'first_time_guest',
        label: 'Are you a first time guest?',
        type: 'radio',
        required: true,
        options: ['Yes', 'No'],
        order: 1,
      },
      {
        id: 'age_group',
        label: 'Age group',
        type: 'select',
        required: true,
        options: ['Jr High', 'High School', 'Young Adult', 'Adult'],
        order: 2,
      },
      {
        id: 'visitor_status',
        label: 'Visiting or Local resident',
        type: 'radio',
        required: true,
        options: ['Visiting', 'Local resident'],
        order: 3,
      },
      {
        id: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        order: 4,
      },
      {
        id: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        order: 5,
      },
    ],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
}

/**
 * Check if organization exists
 * Used to determine if user is new or returning
 */
export async function checkOrganizationExists(orgId: string): Promise<boolean> {
  const orgRef = doc(db, 'organizations', orgId);
  const orgSnap = await getDoc(orgRef);
  return orgSnap.exists();
}

/**
 * Get organization by ID
 */
export async function getOrganization(
  orgId: string
): Promise<Organization | null> {
  const orgRef = doc(db, 'organizations', orgId);
  const snapshot = await getDoc(orgRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as Organization;
}

/**
 * Update organization branding
 */
export async function updateOrganizationBranding(
  orgId: string,
  branding: { themeColor?: string; logoUrl?: string }
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, { branding });
}

/**
 * Get organization subscription status
 * Returns null if no subscription exists
 */
export async function getSubscriptionStatus(
  orgId: string
): Promise<SubscriptionSnapshot | null> {
  const subRef = doc(db, 'organizations', orgId, 'subscription', 'current');
  const snapshot = await getDoc(subRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as SubscriptionSnapshot;
}

/**
 * Check if organization has active subscription or trial
 */
export async function hasActiveAccess(orgId: string): Promise<boolean> {
  const subscription = await getSubscriptionStatus(orgId);
  return isActiveSubscription(subscription);
}

/**
 * Update organization name
 */
export async function updateOrganizationName(
  orgId: string,
  name: string
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    name,
  });
}

/**
 * Upload organization logo to Firebase Storage and update Firestore
 */
export async function updateOrganizationLogo(
  orgId: string,
  file: File
): Promise<string> {
  // Upload to Firebase Storage
  const storageRef = ref(storage, `organizations/${orgId}/logo`);
  await uploadBytes(storageRef, file);

  // Get download URL
  const logoUrl = await getDownloadURL(storageRef);

  // Update Firestore
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    'branding.logoUrl': logoUrl,
  });

  return logoUrl;
}

/**
 * Remove organization logo from Firebase Storage and Firestore
 */
export async function removeOrganizationLogo(orgId: string): Promise<void> {
  // Delete from Firebase Storage
  const storageRef = ref(storage, `organizations/${orgId}/logo`);
  try {
    await deleteObject(storageRef);
  } catch (error: any) {
    // Ignore if file doesn't exist
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }

  // Update Firestore to remove logo URL
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    'branding.logoUrl': null,
  });
}

/**
 * Update organization admin PIN
 */
export async function updateOrganizationPin(
  orgId: string,
  adminPin: string
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    adminPin,
  });
}

/**
 * Toggle weekly recap email delivery
 */
export async function updateWeeklyRecapEnabled(
  orgId: string,
  enabled: boolean
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    weeklyRecapEnabled: enabled,
  });
}

/**
 * Toggle whether the photo booth mirrors the camera preview and captured
 * photos (selfie style). When off, text in frame reads correctly.
 */
export async function updateMirrorPreview(
  orgId: string,
  enabled: boolean
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    mirrorPreview: enabled,
  });
}

/**
 * Replace the list of extra emails that receive the weekly recap
 * (in addition to the organization's account email)
 */
export async function updateWeeklyRecapRecipients(
  orgId: string,
  recipients: string[]
): Promise<void> {
  const orgRef = doc(db, 'organizations', orgId);
  await updateDoc(orgRef, {
    weeklyRecapRecipients: recipients,
  });
}
